/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `B2BEventDetails` for the Caintra Connect frontend.
 * @module pages/B2BEventDetails
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/B2BEventDetails.module.css";

import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";

import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { getUser } from "../logic/user";
import {
  getB2BEventForUser,
  getParticipatingCompanies,
  registerB2BEvent,
  cancelB2BEventAppointment,
  getB2BEventPriceForUser,
} from "../logic/b2bEvents";
import ViewB2BRegistrationModal from "../modals/ViewB2BRegistrationModal/index.jsx";
import B2BEventPaymentModal from "../modals/B2BEventPaymentModal/index.jsx";
import ViewB2BRegistrationView from "../components/ViewB2BRegistrationView/index.jsx";
import ConfirmationModal from "../modals/ConfirmationModal/index.jsx";

/**
 * Maps `userAppointments` from GET /b2bEvent/{eventId}/user/{userId} to the shape expected by row helpers
 * (`idCita` mirrors `_id` for cancel/view flows).
 *
 * @param {Array<Record<string, unknown>>} [userAppointments=[]] - Raw appointments from the API.
 * @returns {Array<Record<string, unknown>>} Appointments including `idCita`.
 */
const mapUserAppointmentsToAttendances = (userAppointments = []) =>
  (userAppointments || []).map((ap) => ({
    ...ap,
    idCita: ap._id,
  }));

/**
 * Page: single B2B event — loads user, event, buyer rows, and participantes; supports register,
 * cancel asistencia, and ver registro.
 *
 * @returns {import("react").ReactElement}
 */
const B2BEventDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [eventData, setEventData] = useState(null);
  const [participatingCompanies, setParticipatingCompanies] = useState(null);
  const [myAttendances, setMyAttendances] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewRegistrationModalOpen, setViewRegistrationModalOpen] =
    useState(false);
  const [showMobileRegistrationView, setShowMobileRegistrationView] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [registeringId, setRegisteringId] = useState(null);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [rowToCancel, setRowToCancel] = useState(null);
  const [cancellingAssistance, setCancellingAssistance] = useState(false);
  const cancelConfirmationDialogRef = useRef(null);
  const [b2bPaymentModalOpen, setB2bPaymentModalOpen] = useState(false);
  const [b2bPaymentAppointment, setB2bPaymentAppointment] = useState(null);
  const [registerError, setRegisterError] = useState("");

  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  /**
   * Derives a coarse layout bucket from the current viewport width (evaluated once per render).
   *
   * @returns {"smallMobile"|"mediumMobile"|"laptop"|"tablet"|"desktop"}
   */
  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    }
    if (isMediumMobile) {
      return "mediumMobile";
    }
    if (isLaptop) {
      return "laptop";
    }
    if (isTablet) {
      return "tablet";
    }
    return "desktop";
  };

  /**
   * Width passed to `SearchBar` for the given device bucket.
   *
   * @param {string} device - Value from {@link getDevice}.
   * @returns {string} CSS width (percentage).
   */
  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "90%";
    }
    if (device === "tablet") {
      return "73%";
    }
    if (device === "mediumMobile") {
      return "90%";
    }
    if (device === "smallMobile") {
      return "90%";
    }
    return "90%";
  };

  const applyEnrichedEvent = useCallback((enriched) => {
    if (!enriched) {
      return;
    }
    setEventData(enriched);
    setMyAttendances(
      mapUserAppointmentsToAttendances(enriched.userAppointments),
    );
  }, []);

  /**
   * Reloads enriched event payload (appointments + executive availability) after register/cancel.
   *
   * @returns {Promise<void>}
   */
  const reloadEnrichedEvent = useCallback(async () => {
    const userId = localStorage.getItem("userId");
    const enriched = await getB2BEventForUser(eventId, userId, navigate);
    if (enriched) {
      applyEnrichedEvent(enriched);
    }
  }, [eventId, navigate, applyEnrichedEvent]);

  /**
   * Fetches current user, enriched B2B event for this user, then participating companies.
   *
   * @returns {Promise<void>}
   */
  const fetchData = useCallback(async () => {
    try {
      const userId = localStorage.getItem("userId");
      const userData = await getUser(userId, navigate);
      if (userData) {
        setCurrentUser(userData);
      }

      const enriched = await getB2BEventForUser(eventId, userId, navigate);
      if (!enriched) {
        navigate("/events", { replace: true });
        return;
      }
      applyEnrichedEvent(enriched);

      await getParticipatingCompanies(
        eventId,
        navigate,
        setParticipatingCompanies,
      );
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  }, [eventId, navigate, applyEnrichedEvent]);

  /**
   * Loads event and related data on mount / when route deps change; always clears loading in `finally`.
   *
   * @returns {Promise<void>}
   */
  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchData();
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
  }, [fetchData]);

  /**
   * Viewport ≤1023px (mobile + tablet): full-page registration view; wider viewports use the modal
   *
   * @returns {boolean}
   */
  const isMobileOrTabletViewport = () =>
    typeof window !== "undefined" && window.innerWidth <= 1023;

  /**
   * When resizing across the tablet/desktop boundary, keep registration UI open by switching
   * between full-page view and modal.
   */
  useEffect(() => {
    const handleResize = () => {
      if (showMobileRegistrationView && !isMobileOrTabletViewport()) {
        setShowMobileRegistrationView(false);
        setViewRegistrationModalOpen(true);
      } else if (
        viewRegistrationModalOpen &&
        isMobileOrTabletViewport() &&
        selectedRow?.appointment
      ) {
        setViewRegistrationModalOpen(false);
        setShowMobileRegistrationView(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    showMobileRegistrationView,
    viewRegistrationModalOpen,
    selectedRow?.appointment,
  ]);

  /**
   * One UI row per unique `ejecutivoComprador` across participating buyer companies (deduped by `_id`).
   * Inactive companies are omitted unless the user already has a cita with that executive.
   */
  const flattenedRows = useMemo(() => {
    if (!participatingCompanies || !Array.isArray(participatingCompanies)) {
      return [];
    }
    const rows = [];
    for (const company of participatingCompanies) {
      const companyActive = company.activo !== false;
      const ejecutivos = company.ejecutivosCompradores ?? [];
      const seenIds = new Set();
      for (const ejecutivo of ejecutivos) {
        const id = ejecutivo._id;
        if (!id || seenIds.has(id)) {
          continue;
        }
        seenIds.add(id);
        const hasExistingCita = myAttendances.some(
          (a) =>
            a.idEmpresaCompradora === company.idEmpresaCompradora &&
            a.idUsuarioComprador === id,
        );
        if (!companyActive && !hasExistingCita) {
          continue;
        }
        rows.push({
          idEmpresaCompradora: company.idEmpresaCompradora,
          nombreComercial: company.nombreComercial,
          ejecutivoComprador: ejecutivo,
          idUsuarioComprador: ejecutivo._id,
        });
      }
    }
    return rows;
  }, [participatingCompanies, myAttendances]);

  /**
   * `flattenedRows` filtered by `searchQuery` (company name or executive name/email).
   */
  const filteredParticipantsList = useMemo(() => {
    if (!flattenedRows.length) {
      return [];
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return flattenedRows;
    }
    return flattenedRows.filter((row) => {
      const name = (row.nombreComercial || "").toLowerCase();
      const execName = (
        row.ejecutivoComprador?.nombre ||
        row.ejecutivoComprador?.correoElectronico ||
        ""
      ).toLowerCase();
      return name.includes(q) || execName.includes(q);
    });
  }, [flattenedRows, searchQuery]);

  /**
   * Finds the current user's attendance for a participant row (same buyer company + buyer user).
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {Record<string, unknown>|undefined} Matching attendance or `undefined`.
   */
  const getAttendanceForRow = (row) => {
    return myAttendances.find(
      (a) =>
        a.idEmpresaCompradora === row.idEmpresaCompradora &&
        a.idUsuarioComprador === row.idUsuarioComprador,
    );
  };

  /**
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {boolean} Whether the user already has an appointment for this row.
   */
  const isRegisteredForRow = (row) => Boolean(getAttendanceForRow(row));

  /**
   * Priced event and payment not yet confirmed (`pagado` not strictly true).
   *
   * @param {Record<string, unknown>|undefined} attendance
   * @returns {boolean}
   */
  const attendanceNeedsPayment = (attendance) => {
    if (!attendance) {
      return false;
    }
    return (
      getB2BEventPriceForUser(eventData, currentUser) > 0 &&
      attendance.pagado !== true
    );
  };

  /**
   * Opens payment modal for an existing unpaid appointment (same row).
   *
   * @param {Record<string, unknown>} row
   */
  const handleMakePayment = (row) => {
    const attendance = getAttendanceForRow(row);
    const id = attendance?._id || attendance?.idCita;
    if (!id) {
      return;
    }
    setB2bPaymentAppointment({ ...attendance, _id: id });
    setB2bPaymentModalOpen(true);
  };

  /**
   * Same buyer user as `row` but a different buyer company — used to disable duplicate registration.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {boolean}
   */
  const hasRegistrationForSameBuyerUserOnly = (row) =>
    myAttendances.some(
      (a) =>
        a.idUsuarioComprador === row.idUsuarioComprador &&
        a.idEmpresaCompradora !== row.idEmpresaCompradora,
    );

  /**
   * Same buyer company as `row` but a different ejecutivo — used to disable duplicate registration.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {boolean}
   */
  const hasRegistrationForSameCompanyOnly = (row) =>
    myAttendances.some(
      (a) =>
        a.idEmpresaCompradora === row.idEmpresaCompradora &&
        a.idUsuarioComprador !== row.idUsuarioComprador,
    );

  const userDurationInvalid = eventData?.duracionCitaMinutosUsuario === 0;

  /**
   * Whether the backend reports a bookable gap for this buyer executive and the current user's slot length.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {boolean}
   */
  const canRegisterWithExecutive = (row) => {
    if (userDurationInvalid) {
      return false;
    }
    const list = eventData?.disponibilidadEjecutivosRegistro;
    if (!Array.isArray(list)) {
      return false;
    }
    const entry = list.find(
      (item) =>
        item.idUsuarioComprador === row.idUsuarioComprador &&
        item.idEmpresaCompradora === row.idEmpresaCompradora,
    );
    return entry?.canRegister === true;
  };

  /**
   * Registers the current user for the given buyer row, then reloads state.
   * Does not open the payment modal; if the slot requires payment, the user uses **Make payment** then {@link handleMakePayment}.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {Promise<void>}
   */
  const handleRegister = async (row) => {
    const key = `${row.idEmpresaCompradora}-${row.idUsuarioComprador}`;
    setRegisteringId(key);
    setRegisterError("");
    try {
      const result = await registerB2BEvent(
        eventId,
        row.idEmpresaCompradora,
        row.idUsuarioComprador,
        navigate,
        currentUser?.idEmpresa,
      );
      if (result?.error === "auth") {
        return;
      }
      if (result?.data) {
        await reloadEnrichedEvent();
      } else {
        if (result?.code === "B2B_COMPANY_INACTIVE") {
          setRegisterError(
            t("pages.b2bEventDetails.companyInactiveRegisterError"),
          );
          await getParticipatingCompanies(
            eventId,
            navigate,
            setParticipatingCompanies,
          );
        } else {
          logger.error("Error", {
            err: sanitizeError(result?.error || "Failed to register"),
          });
        }
      }
    } finally {
      setRegisteringId(null);
    }
  };

  /**
   * Calls cancel API for the row's attendance and reloads enriched event state on success.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   * @returns {Promise<void>}
   */
  const performCancelAssistance = async (row) => {
    const attendance = getAttendanceForRow(row);
    if (!attendance?.idCita || !currentUser?._id) {
      return;
    }
    const idCitaToRemove = attendance.idCita;
    const result = await cancelB2BEventAppointment(
      currentUser._id,
      eventId,
      idCitaToRemove,
      navigate,
    );
    if (result?.data || result === undefined) {
      await reloadEnrichedEvent();
    }
  };

  /**
   * Opens the cancel confirmation dialog for the given row.
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   */
  const handleCancelAssistanceClick = (row) => {
    setRowToCancel(row);
    setCancelConfirmationOpen(true);
  };

  /**
   * Confirms cancel: runs {@link performCancelAssistance} for `rowToCancel` and closes the dialog.
   *
   * @returns {Promise<void>}
   */
  const handleConfirmCancelAssistance = async () => {
    if (!rowToCancel) {
      return;
    }
    setCancellingAssistance(true);
    try {
      await performCancelAssistance(rowToCancel);
      setCancelConfirmationOpen(false);
      setRowToCancel(null);
    } finally {
      setCancellingAssistance(false);
    }
  };

  /**
   * Closes cancel dialog and clears `rowToCancel` unless a cancel request is in flight.
   */
  const handleCloseCancelConfirmation = () => {
    if (!cancellingAssistance) {
      setCancelConfirmationOpen(false);
      setRowToCancel(null);
    }
  };

  /**
   * Opens the view-registration modal with appointment details for this row (if the user is registered).
   *
   * @param {Record<string, unknown>} row - Flattened participant row.
   */
  const handleViewRegistration = (row) => {
    const attendance = getAttendanceForRow(row);
    if (!attendance) {
      return;
    }
    setSelectedRow({
      ...row,
      appointment: {
        _id: attendance.idCita,
        fecha: attendance.fecha,
        horaInicio: attendance.horaInicio,
        horaFin: attendance.horaFin,
        posicionEnFilaVirtual: attendance.posicionEnFilaVirtual,
        nombreUsuarioComprador: attendance.nombreUsuarioComprador,
        nombreComercial: row.nombreComercial,
      },
    });
    if (isMobileOrTabletViewport()) {
      setShowMobileRegistrationView(true);
    } else {
      setViewRegistrationModalOpen(true);
    }
  };

  /**
   * Closes the full-page registration view (mobile/tablet) and clears selection.
   */
  const handleBackFromMobileRegistration = () => {
    setShowMobileRegistrationView(false);
    setSelectedRow(null);
  };

  /**
   * @param {import("react").ChangeEvent<HTMLInputElement>} event - Search input change.
   */
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  /** Navigates to the events list. */
  const handleBack = () => {
    navigate("/events");
  };

  return (
    <div className={styles.container}>
      {showMobileRegistrationView && selectedRow?.appointment && eventData ? (
        <ViewB2BRegistrationView
          navigate={navigate}
          event={eventData}
          appointment={selectedRow.appointment}
          onBack={handleBackFromMobileRegistration}
        />
      ) : (
        <>
          <div className={styles.header}>
            <div className={styles.languages}>
              <NotificationBell navigate={navigate} />
              <LanguageSelector />
            </div>

            <div className={styles.headerInfoContainer}>
              <div className={styles.headerInfo}>
                <button
                  type="button"
                  className={styles.returnButtonContainer}
                  onClick={handleBack}
                >
                  <ChevronLeftIcon className={styles.returnButtonIcon} />
                  <span className={styles.returnButtonText}>
                    {t("common.goBack")}
                  </span>
                </button>
              </div>
              <div className={styles.headerInfo}>
                <h1 className={styles.title}>{eventData?.nombre || ""}</h1>
              </div>
              <div className={styles.searchSection}>
                <SearchBar
                  placeholder={
                    isSmallMobile || isMediumMobile
                      ? t("pages.b2bEventDetails.mobileSearchText")
                      : t("pages.b2bEventDetails.searchText")
                  }
                  value={searchQuery}
                  onChange={handleSearch}
                  width={getSearchBarWidth(getDevice())}
                />
              </div>
            </div>
          </div>

          <div className={styles.participantsListContainer}>
            <span className={styles.participantsListTitle}>
              {t("pages.b2bEventDetails.participants")}
            </span>
            {registerError && (
              <p className={styles.registerError}>{registerError}</p>
            )}
            <div className={styles.participantsList}>
              {filteredParticipantsList?.length === 0 && (
                <div className={styles.noResults}>
                  <p>{t("pages.b2bEventDetails.noResults")}</p>
                </div>
              )}
              {filteredParticipantsList?.length > 0 &&
                filteredParticipantsList.map((row, index) => {
                  const isRegistered = isRegisteredForRow(row);
                  const attendance = getAttendanceForRow(row);
                  const registerDisabled =
                    hasRegistrationForSameBuyerUserOnly(row) ||
                    hasRegistrationForSameCompanyOnly(row) ||
                    isRegistered ||
                    !canRegisterWithExecutive(row);
                  const key = `${row.idEmpresaCompradora}-${row.idUsuarioComprador}-${index}`;
                  const rowRegisterKey = `${row.idEmpresaCompradora}-${row.idUsuarioComprador}`;
                  const isRegistering = registeringId === rowRegisterKey;
                  const isAnyRegisterInFlight = registeringId !== null;
                  const showMakePayment =
                    isRegistered && attendanceNeedsPayment(attendance);

                  return (
                    <div key={key} className={styles.participantCard}>
                      <div className={styles.participantDetails}>
                        <span className={styles.participantName}>
                          {row.nombreComercial}
                        </span>
                        <span className={styles.detailLabel}>
                          <strong>1</strong>{" "}
                          {t("pages.b2bEventDetails.executive")}
                        </span>
                      </div>
                      <div className={styles.buttonsContainer}>
                        {isRegistered ? (
                          <>
                            {showMakePayment ? (
                              <button
                                type="button"
                                className={styles.makePaymentButton}
                                onClick={() => handleMakePayment(row)}
                              >
                                {t("pages.businessEventDetails.makePayment")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={styles.viewRegistrationButton}
                                onClick={() => handleViewRegistration(row)}
                              >
                                {t("pages.b2bEventDetails.viewRegistration")}
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            type="button"
                            className={styles.registerButton}
                            onClick={() => handleRegister(row)}
                            disabled={registerDisabled || isAnyRegisterInFlight}
                          >
                            {isRegistering
                              ? t("pages.b2bEventDetails.registering")
                              : t("pages.b2bEventDetails.register")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {viewRegistrationModalOpen && selectedRow?.appointment && (
            <ViewB2BRegistrationModal
              open={viewRegistrationModalOpen}
              onClose={() => {
                setViewRegistrationModalOpen(false);
                setSelectedRow(null);
              }}
              event={eventData}
              appointment={selectedRow.appointment}
            />
          )}
        </>
      )}

      {eventData && currentUser && b2bPaymentAppointment && (
        <B2BEventPaymentModal
          open={b2bPaymentModalOpen}
          onClose={() => {
            setB2bPaymentModalOpen(false);
            setB2bPaymentAppointment(null);
            void reloadEnrichedEvent();
          }}
          b2bEvent={eventData}
          b2bAppointment={b2bPaymentAppointment}
          user={currentUser}
        />
      )}

      <ConfirmationModal
        dialogRef={cancelConfirmationDialogRef}
        open={cancelConfirmationOpen}
        onClose={handleCloseCancelConfirmation}
        onConfirm={handleConfirmCancelAssistance}
        title={t("pages.b2bEventDetails.cancelConfirmationTitle")}
        message={t("pages.b2bEventDetails.cancelConfirmationMessage")}
        confirmButtonText={t("pages.b2bEventDetails.cancelAssistance")}
        cancelButtonText={t("common.cancel")}
        isLoading={cancellingAssistance}
        loadingButtonText={t("pages.b2bEventDetails.cancelling")}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default B2BEventDetails;
